import React from 'react';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import { motion } from 'framer-motion';

/**
 * Animated loader component with smooth transitions
 * @param {Object} props - Component props
 * @param {boolean} props.loading - Whether the loader is active
 * @param {string} props.message - Optional message to display
 * @param {ReactNode} props.children - Content to display when not loading
 * @param {Object} props.sx - Additional styles for the container
 * @returns {JSX.Element} - Animated loader component
 */
const AnimatedLoader = ({ 
  loading = false, 
  message = 'Loading...', 
  children, 
  sx = {} 
}) => {
  const theme = useTheme();

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.3,
        when: 'beforeChildren',
        staggerChildren: 0.1
      }
    },
    exit: { 
      opacity: 0,
      transition: { 
        duration: 0.2,
        when: 'afterChildren',
        staggerChildren: 0.05,
        staggerDirection: -1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        type: 'spring',
        stiffness: 300, 
        damping: 24
      }
    },
    exit: { 
      y: -20, 
      opacity: 0,
      transition: { duration: 0.2 }
    }
  };

  // Spinner animation
  const spinnerVariants = {
    animate: {
      rotate: 360,
      transition: {
        repeat: Infinity,
        duration: 1.4,
        ease: 'linear'
      }
    }
  };

  // Pulsing animation for the message
  const pulseVariants = {
    initial: { opacity: 0.7 },
    animate: {
      opacity: 1,
      transition: {
        repeat: Infinity,
        repeatType: 'reverse',
        duration: 1,
      }
    }
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center',
        height: '100%',
        width: '100%',
        position: 'relative',
        ...sx
      }}
    >
      {loading ? (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <motion.div 
            variants={itemVariants}
            style={{ display: 'flex', justifyContent: 'center' }}
          >
            <motion.div variants={spinnerVariants} animate="animate">
              <CircularProgress 
                size={56}
                thickness={4}
                sx={{ color: theme.palette.primary.main }} 
              />
            </motion.div>
          </motion.div>
          
          {message && (
            <motion.div 
              variants={itemVariants}
              style={{ marginTop: 16, textAlign: 'center' }}
            >
              <motion.div variants={pulseVariants} initial="initial" animate="animate">
                <Typography 
                  variant="body1" 
                  color="textSecondary"
                  sx={{ fontWeight: 500 }}
                >
                  {message}
                </Typography>
              </motion.div>
            </motion.div>
          )}
        </motion.div>
      ) : children}
    </Box>
  );
};

export default AnimatedLoader; 